/**
 * MiniappIconEditorDialog — the editor for a mini-app's custom icon. The
 * foreground is EITHER text/Noto-emoji OR a Lucide icon (mutually exclusive),
 * on a solid/vertical-gradient squircle. Portal dialog that follows the
 * FileInfoDialog pattern (createPortal to <body>, workspace theme class on the
 * panel, backdrop, Escape-to-close). Writes a single schema-valid
 * `draft: MiniappIconSpec` through every control and persists via
 * `miniappApi.setIcon`. The emoji / icon / color pickers are glass popovers
 * (see MiniappIconPickers). See docs/plans/2026-07-15_(completed)_MINIAPP_ICON.md.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import { X, Smile, Loader2, Shapes, Image as ImageIcon, Ban, Upload, HardDrive } from 'lucide-react'
import {
  useTheme,
  getThemeClass,
  useToast,
  MiniappIcon,
  ensureNotoEmoji,
  PlacementPicker,
} from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import {
  MiniappIconSpecSchema,
  parseMiniappIcon,
  defaultIconSpecFor,
  defaultMonogramFor,
  graphemeCount,
  autoTextColor,
  validateIconAssetPick,
  formatIconAssetLimit,
  MINIAPP_ICON_DEFAULT_LUCIDE,
  MINIAPP_ICON_BG_PRESETS,
  MINIAPP_ICON_TEXT_MAX_GRAPHEMES,
  clampIconText,
    MINIAPP_ICON_SVG_MAX_BYTES,
  MINIAPP_ICON_BG_RENDER_DIM,
  MINIAPP_ICON_FG_RENDER_DIM,
  MINIAPP_ICON_BG_ACCEPT,
  MINIAPP_ICON_FG_ACCEPT,
  stripMiniappFolderExt,
  type IconAssetPickError,
  type MiniappIconSpec,
  type Placement,
} from '@papan/types'
import { miniappApi, filesApi } from '@papan/files/client'
import { EmojiPickerPopover, IconPickerPopover, ColorField, preloadEmojiData } from './MiniappIconPickers'
import { IconAssetDrivePicker } from './IconAssetDrivePicker'
import { downscaleIconImage, encodePortableIconAsset } from './iconAssetDownscale'


/** Which panel a slot is showing. UI state — see the `fgTab`/`bgTab` note below. */
type FgTab = 'text' | 'icon' | 'image' | 'blank'
type BgTab = 'solid' | 'gradient' | 'image'

/** Drop the `icon` key from a spec (switching to text mode). */
function stripIcon(d: MiniappIconSpec): MiniappIconSpec {
  const { icon: _icon, ...rest } = d
  return rest
}
/** Drop the `text` key from a spec (switching to icon mode). */
function stripText(d: MiniappIconSpec): MiniappIconSpec {
  const { text: _text, ...rest } = d
  return rest
}
/** Drop ALL foreground keys (text/icon/image) — switching foreground mode. */
function stripForeground(d: MiniappIconSpec): MiniappIconSpec {
  const { text: _t, icon: _i, image: _im, ...rest } = d
  return rest
}

interface MiniappIconEditorDialogProps {
  file: { id: string; name: string; metadataJson?: string | null }
  onClose: () => void
  onSaved?: (icon: MiniappIconSpec | null) => void
  /**
   * PRODUCE mode (#788): hand the designed spec back instead of saving it onto
   * `file`. The whole editor is the same — this only changes what the primary
   * button does, because "design an icon to put in my page" and "change this
   * app's icon" are the same act of designing and two different destinations.
   *
   * Uploaded art is inlined as `data:` URIs rather than written into an app
   * folder: there is no folder to write to, and the metadata has to be
   * self-contained anyway (that is what makes the render pure).
   */
  onProduce?: (icon: MiniappIconSpec, assets: { bg?: string; fg?: string }) => void
  /**
   * Art the caller already HAS, as `data:` URIs — produce mode only.
   *
   * There is no app folder to fetch a saved asset from, so without this an icon
   * being EDITED would open with its picture missing and hand back a spec that
   * still claims one: the author changes a colour and their image is gone. Seeds
   * the preview, and rides back out untouched in `onProduce` for any slot the
   * author did not replace.
   */
  initialAssets?: { bg?: string; fg?: string }
  /** Tenant whose Drive the "Papan Drive" image source browses. OPTIONAL: without
   *  it that button is hidden and only computer upload is offered — graceful
   *  degradation for any future caller with no tenant in scope. Every current
   *  call site threads it (a listing prop, or `getActiveTenantId()` from
   *  @papan/api-client), so the Drive source shows on all surfaces the editor
   *  opens from — drive rows, Home/Projects/MiniApps cards, chat-home, the
   *  workspace explorer, the taskbar edit action, and the mini-app settings pane. */
  tenantId?: string
  /**
   * WHERE the produced icon should land — produce mode only (#788, Chu's third
   * review).
   *
   * Deliberately opaque to this dialog: it renders a labelled `<select>` and
   * reports the chosen value, and knows nothing about design mode, the page, or
   * what "after this element" means. The CALLER owns those semantics, because
   * they differ per surface — the editor resolves against a live design
   * selection, the chat card against the selection the workspace is sharing with
   * it. Baking either in here would put a second copy of the placement rules in
   * the one component both surfaces share.
   *
   * Absent → no control. That is the honest rendering for a caller with nowhere
   * to choose between (source-mode insert, or no selection at all).
   */
  placement?: {
    label: string
    options: ReadonlyArray<{ value: Placement; label: string }>
    value: Placement
    onChange: (value: Placement) => void
  }
}

// Six tasteful fixed gradient pairs (top → bottom), from @papan/types so the
// compact creator in the chat offers the same set. Two more are derived from the
// name's default spec at render time, for eight presets total.
const FIXED_PRESETS = MINIAPP_ICON_BG_PRESETS

export function MiniappIconEditorDialog({ file, onClose, onSaved, onProduce, initialAssets, tenantId, placement }: MiniappIconEditorDialogProps) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()

  const displayName = stripMiniappFolderExt(file.name)

  const [draft, setDraft] = useState<MiniappIconSpec>(
    () => parseMiniappIcon(file.metadataJson) ?? defaultIconSpecFor(file.name),
  )
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [iconOpen, setIconOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  // Staged (picked, not yet uploaded) image assets — object URLs drive the live
  // LOCAL preview; the actual upload happens on Save.
  const [stagedBg, setStagedBg] = useState<{ file: File; url: string } | null>(null)
  const [stagedFg, setStagedFg] = useState<{ file: File; url: string } | null>(null)
  // Per-slot rejection message for the LAST picked file (wrong type / too big).
  // Rendered in that slot's helper-text line and blocks Save. Cleared by a valid
  // pick or by switching that slot away from an image.
  const [assetError, setAssetError] = useState<{ bg: string | null; fg: string | null }>({ bg: null, fg: null })
  // Object URLs for assets ALREADY SAVED on the server. The preview composites the
  // draft locally, so it needs the saved image bytes to draw a slot the user hasn't
  // re-picked this session — e.g. "keep the saved background, swap the foreground
  // to text". `icon.png` can't supply that: it's the saved composite with the old
  // foreground baked in, which is exactly why the preview used to freeze.
  const [savedAssets, setSavedAssets] = useState<{ bg: string | null; fg: string | null }>(
    () => ({ bg: initialAssets?.bg ?? null, fg: initialAssets?.fg ?? null }),
  )
  // True while the saved assets are still in flight. Seeded from the STORED spec at
  // FIRST RENDER (not in an effect) — otherwise the placeholder gets one paint
  // before the effect runs, which is exactly the flash of default-gradient icon
  // this exists to prevent.
  const [savedLoading, setSavedLoading] = useState(() => {
    // Nothing to wait for when the caller handed the art over.
    if (initialAssets) return false
    const stored = parseMiniappIcon(file.metadataJson)
    return !!stored && (stored.bg.type === 'image' || stored.image === true)
  })
  const bgFileRef = useRef<HTMLInputElement>(null)
  const fgFileRef = useRef<HTMLInputElement>(null)
  const stagedUrlsRef = useRef<string[]>([])
  useEffect(() => () => { stagedUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)) }, [])

  // Fetch the saved assets ONCE per open, for whichever slots the STORED spec
  // references (the draft's own slots change as the user edits; what's on the
  // server does not). Failures are silent — the preview just falls back to the
  // spec's background, which is a placeholder, not data loss.
  useEffect(() => {
    // The caller's art IS the saved art here — there is no folder to read.
    if (initialAssets) { setSavedLoading(false); return }
    const stored = parseMiniappIcon(file.metadataJson)
    if (!stored) { setSavedLoading(false); return }
    let alive = true
    const want: Array<'bg' | 'fg'> = []
    if (stored.bg.type === 'image') want.push('bg')
    if (stored.image === true) want.push('fg')
    if (!want.length) { setSavedLoading(false); return }
    void Promise.all(want.map(async (kind) => {
      const blob = await miniappApi.getIconAsset(file.id, kind).catch(() => null)
      if (!blob || !alive) return
      const url = URL.createObjectURL(blob)
      stagedUrlsRef.current.push(url) // revoked by the unmount cleanup above
      setSavedAssets((s) => ({ ...s, [kind]: url }))
    })).finally(() => { if (alive) setSavedLoading(false) })
    return () => { alive = false }
  }, [file.id, file.metadataJson, initialAssets])

  const emojiBtnRef = useRef<HTMLButtonElement>(null)
  const iconBtnRef = useRef<HTMLButtonElement>(null)

  // Remember the last text / icon so toggling modes restores the prior choice
  // instead of resetting to a placeholder every time.
  const lastText = useRef<string>(draft.text ?? defaultMonogramFor(file.name))
  const lastIcon = useRef<string>(draft.icon ?? MINIAPP_ICON_DEFAULT_LUCIDE)
  useEffect(() => { if (draft.text) lastText.current = draft.text }, [draft.text])
  useEffect(() => { if (draft.icon) lastIcon.current = draft.icon }, [draft.icon])

  // Which panel each slot shows. EXPLICIT UI state, not derived from the draft:
  // "Image" is a tab you can sit on with nothing chosen yet, so there's no draft
  // shape that represents it. (It used to be derived, which forced the Image
  // button to fire the OS file dialog immediately — there was no way to *be* in
  // image mode without an image.)
  //
  // The draft still only goes image-backed once an asset actually exists, so an
  // empty Image tab can never persist a spec pointing at a file that isn't there.
  // The preview and Save therefore always agree with each other.
  const [fgTab, setFgTab] = useState<FgTab>(() =>
    draft.image ? 'image' : draft.icon ? 'icon' : draft.text ? 'text' : 'blank')
  const [bgTab, setBgTab] = useState<BgTab>(() => draft.bg.type)
  /** Which slot's Drive picker is open (null = none). */
  const [drivePicker, setDrivePicker] = useState<'bg' | 'fg' | null>(null)
  const [driveLoading, setDriveLoading] = useState(false)
  /** Per-slot: shrinking a just-picked image (decode + re-encode is not instant
   *  for a 4000px photo, and the panel must not look inert meanwhile). */
  const [staging, setStaging] = useState<{ bg: boolean; fg: boolean }>({ bg: false, fg: false })

  // Faithful preview: preload the Noto face for the selected style up front so
  // emoji render in the right font as soon as the dialog is on screen.
  useEffect(() => {
    void ensureNotoEmoji(draft.emojiStyle)
  }, [draft.emojiStyle])

  // Warm the (large, ~430KB) emoji data set as soon as the editor opens — NOT
  // gated on text mode. A default app opens the editor in ICON mode, so a
  // mode-gated preload wouldn't start until the user is already at the emoji
  // button, and the first open would then stall on a cold `import()` — the picker
  // shows only a spinner, which reads as "the emoji button does nothing for the
  // first few clicks." Preloading on mount means the data is ready by the time the
  // picker opens. `preloadEmojiData` is memoized, so this costs one import total.
  useEffect(() => {
    void preloadEmojiData()
  }, [])

  // Category id → localized label for the emoji picker's section headers + nav.
  const emojiCategoryLabels: Record<string, string> = {
    people: t('iconEditor.emojiCat.people'),
    nature: t('iconEditor.emojiCat.nature'),
    foods: t('iconEditor.emojiCat.foods'),
    activity: t('iconEditor.emojiCat.activity'),
    places: t('iconEditor.emojiCat.places'),
    objects: t('iconEditor.emojiCat.objects'),
    symbols: t('iconEditor.emojiCat.symbols'),
    flags: t('iconEditor.emojiCat.flags'),
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const gcount = graphemeCount(draft.text ?? '')
  const overLimit = gcount > MINIAPP_ICON_TEXT_MAX_GRAPHEMES
  const parsed = MiniappIconSpecSchema.safeParse(draft)
  // A rejected pick blocks Save outright: the draft is untouched (so it WOULD
  // save cleanly), but saving while an error is on screen would silently discard
  // the file the user just chose.
  const canSave = parsed.success && !saving && !assetError.bg && !assetError.fg

  const svgLabel = formatIconAssetLimit(MINIAPP_ICON_SVG_MAX_BYTES)

  /** A pick rejection → the message shown in that slot's helper-text line.
   *  Reports the LIMIT, not the file's own size: the two round to the same label
   *  just over the cap ("That file is 1 MB — the max is 1 MB"), and the limit is
   *  the only half the user can act on. `maxBytes` is the cap for THAT file's
   *  format, so an oversized SVG correctly names 256 KB rather than the hint's 1 MB. */
  const pickErrorMessage = (kind: 'bg' | 'fg', err: IconAssetPickError): string => {
    if (err.code === 'type') {
      return kind === 'bg'
        ? t('iconEditor.assetErrorTypeBg', { defaultValue: 'Choose a PNG or JPEG file.' })
        : t('iconEditor.assetErrorTypeFg', { defaultValue: 'Choose an SVG or PNG file.' })
    }
    return t('iconEditor.assetErrorSize', {
      defaultValue: 'That file is too large — max {{max}}.',
      max: formatIconAssetLimit(err.maxBytes),
    })
  }

  // Stage a picked image locally (object URL) + flip the draft to reference it.
  // The actual upload happens on Save.
  const stageFile = async (kind: 'bg' | 'fg', picked: File) => {
    // A pick is only reachable from that slot's Image tab, but pin the tab anyway
    // so the outcome — the staged file OR the rejection below — is always on a
    // panel that's actually mounted, and can't render into nowhere.
    if (kind === 'fg') setFgTab('image')
    else setBgTab('image')

    // TYPE first, on the ORIGINAL and before any decode: a wrong type is the real
    // problem and shouldn't be reported as a size one, nor be handed to the
    // decoder. `size: 0` keeps this pass type-only.
    const badType = validateIconAssetPick(kind, { name: picked.name, type: picked.type, size: 0 })
    if (badType) {
      setAssetError((e) => ({ ...e, [kind]: pickErrorMessage(kind, badType) }))
      return
    }

    // Shrink to the slot's render target before the size check, so the cap applies
    // to what we'd actually UPLOAD rather than to the camera-sized original. Falls
    // back to the original on any failure; the server re-validates either way.
    setStaging((v) => ({ ...v, [kind]: true }))
    let f: File
    try {
      f = await downscaleIconImage(kind, picked)
    } finally {
      setStaging((v) => ({ ...v, [kind]: false }))
    }

    const bad = validateIconAssetPick(kind, { name: f.name, type: f.type, size: f.size })
    if (bad) {
      // Reject WITHOUT staging or touching the draft — the spec must never
      // reference an asset we won't upload. (Feedback only; the server
      // re-validates the bytes and is the authority.)
      setAssetError((e) => ({ ...e, [kind]: pickErrorMessage(kind, bad) }))
      return
    }
    setAssetError((e) => ({ ...e, [kind]: null }))
    const url = URL.createObjectURL(f)
    stagedUrlsRef.current.push(url)
    if (kind === 'bg') {
      setStagedBg((prev) => { if (prev) URL.revokeObjectURL(prev.url); return { file: f, url } })
      setDraft((d) => ({ ...d, bg: { type: 'image' } }))
    } else {
      setStagedFg((prev) => { if (prev) URL.revokeObjectURL(prev.url); return { file: f, url } })
      setDraft((d) => ({ ...stripForeground(d), image: true }))
    }
  }
  const onPickFile = (kind: 'bg' | 'fg') => (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (f) void stageFile(kind, f)
  }

  /** Switch the foreground tab. Selecting "Image" now just OPENS the tab — the
   *  user picks a source from inside it (computer or Drive). It no longer fires
   *  the OS file dialog on click. */
  const setFgMode = (m: FgTab) => {
    setEmojiOpen(false)
    setIconOpen(false)
    setFgTab(m)
    if (m === 'image') {
      // Coming BACK to a slot that still has an image — staged this session or
      // saved earlier — restores it, the same way text/icon restore from
      // lastText/lastIcon. With no image yet the draft stays foreground-less, so
      // the tab can sit empty without ever persisting a dangling `image: true`.
      if (stagedFg || savedAssets.fg) setDraft((d) => ({ ...stripForeground(d), image: true }))
      return
    }
    setAssetError((e) => ({ ...e, fg: null }))
    // The staged file is deliberately KEPT — `handleSave` uploads only what the
    // draft references, so an abandoned pick is inert rather than an orphan, and
    // toggling tabs doesn't throw away the file the user just chose.
    if (m === 'text') setDraft((d) => ({ ...stripForeground(d), text: clampIconText(lastText.current || defaultMonogramFor(file.name)) }))
    else if (m === 'icon') setDraft((d) => ({ ...stripForeground(d), icon: lastIcon.current || MINIAPP_ICON_DEFAULT_LUCIDE }))
    else setDraft((d) => stripForeground(d)) // 'blank'
  }

  /** Pull a Drive file's bytes and stage them exactly like a computer upload —
   *  same validation, same preview, same upload-on-Save. */
  const pickFromDrive = async (kind: 'bg' | 'fg', sel: { id: string; name: string; mimeType: string | null }) => {
    setDrivePicker(null)
    setDriveLoading(true)
    try {
      const blob = await filesApi.getFileContent(sel.id)
      // Trust Drive's mimeType only when it's actually an image type; a generic
      // `application/octet-stream` would otherwise read as "wrong file type" and
      // reject a perfectly good PNG. Empty type ⇒ validateIconAssetPick falls back
      // to the extension, which is what the user picked by.
      const type = /^image\//i.test(sel.mimeType ?? '') ? (sel.mimeType as string) : ''
      await stageFile(kind, new File([blob], sel.name, { type }))
    } catch {
      setAssetError((e) => ({ ...e, [kind]: t('iconEditor.drivePickFailed', { defaultValue: "Couldn't read that file from Drive." }) }))
    } finally {
      setDriveLoading(false)
    }
  }

  const setText = (raw: string) => setDraft((d) => ({ ...stripIcon(d), text: clampIconText(raw) }))
  const insertEmoji = (native: string) =>
    setDraft((d) => ({ ...stripIcon(d), text: clampIconText((d.text ?? '') + native) }))
  const setIconName = (name: string) => setDraft((d) => ({ ...stripText(d), icon: name }))

  /** Switch the background tab. "Image" opens the tab only — same as the
   *  foreground, the source is chosen from inside it. */
  const setBgType = (type: BgTab) => {
    setBgTab(type)
    if (type === 'image') {
      // Restore an image background we still hold (staged or saved). With none,
      // the draft KEEPS its solid/gradient — a background can't be "empty", so
      // that's the truthful value to preview and to save until an image lands.
      if (stagedBg || savedAssets.bg) setDraft((d) => ({ ...d, bg: { type: 'image' } }))
      return
    }
    setAssetError((e) => ({ ...e, bg: null }))
    setDraft((d) => {
      if (d.bg.type === type) return d
      if (type === 'solid') {
        const color = d.bg.type === 'gradient' ? d.bg.from : d.bg.type === 'solid' ? d.bg.color : '#3b82f6'
        return { ...d, bg: { type: 'solid', color } }
      }
      const base = d.bg.type === 'solid' ? d.bg.color : '#3b82f6'
      return { ...d, bg: { type: 'gradient', from: base, to: base } }
    })
  }

  // NOTE: the tile silhouette (`shape`) is deliberately NOT editable here.
  // It stays a spec field the manifest and the agent tool can set, but the
  // product decision is that a project's tile is the portrait rectangle, so
  // there is no control for it. `shape` absent already means portrait, which
  // is why hiding the control IS the default — nothing needed resetting.

  const setSolid = (color: string) => setDraft((d) => ({ ...d, bg: { type: 'solid', color } }))
  const setGrad = (patch: Partial<{ from: string; to: string }>) =>
    setDraft((d) => (d.bg.type === 'gradient' ? { ...d, bg: { ...d.bg, ...patch } } : d))
  // A glyph colour that was just the readable pick for the OLD background (the default icon
  // stores white) follows the new one — else a light preset leaves a white glyph on white. A
  // colour the person chose themselves stays.
  const applyPreset = (p: { from: string; to: string }) =>
    setDraft((d) => {
      const bg = { type: 'gradient' as const, from: p.from, to: p.to }
      const followed = d.textColor !== undefined && d.textColor.toLowerCase() === autoTextColor(d.bg)
      return { ...d, bg, ...(followed ? { textColor: autoTextColor(bg) } : {}) }
    })

  const autoText = draft.textColor === undefined
  const setAutoText = (auto: boolean) =>
    setDraft((d) => {
      if (auto) { const { textColor: _drop, ...rest } = d; return rest }
      return { ...d, textColor: autoTextColor(d.bg) }
    })
  const setTextColor = (color: string) => setDraft((d) => ({ ...d, textColor: color }))
  const glyphColor = draft.textColor ?? autoTextColor(draft.bg)

  const derived = defaultIconSpecFor(file.name)
  const derivedPair =
    derived.bg.type === 'gradient'
      ? { from: derived.bg.from, to: derived.bg.to }
      : derived.bg.type === 'solid'
        ? { from: derived.bg.color, to: derived.bg.color }
        : { from: '#3b82f6', to: '#3b82f6' }
  const presets: Array<{ from: string; to: string }> = [
    derivedPair,
    { from: derivedPair.to, to: derivedPair.from },
    ...FIXED_PRESETS,
  ]

  const handleSave = async () => {
    if (!parsed.success) return
    setSaving(true)
    try {
      // PRODUCE — nothing is written to a folder; the spec and its art go back to
      // the caller, art inlined so the result stands on its own.
      if (onProduce) {
        const spec = parsed.data
        const assets: { bg?: string; fg?: string } = {}
        // The SLOT decides how each asset is encoded, never the file's own type:
        // a foreground must stay PNG (a JPEG flattens its transparency onto
        // black), and an SVG has to be rasterised, because the renderer this
        // feeds accepts only PNG/JPEG data URIs and refuses anything else
        // outright. `encodePortableIconAsset` throws a sentence for the author
        // when a picture cannot travel; the catch below leaves the editor open
        // with their design intact.
        // A slot the author did not touch keeps the picture it came in with —
        // it is already encoded for travel, and re-encoding it would lose a
        // generation for nothing.
        if (spec.bg.type === 'image') {
          assets.bg = stagedBg ? await encodePortableIconAsset('bg', stagedBg.file) : initialAssets?.bg
        }
        if (spec.image === true) {
          assets.fg = stagedFg ? await encodePortableIconAsset('fg', stagedFg.file) : initialAssets?.fg
        }
        onProduce(spec, assets)
        onClose()
        return
      }
      // Upload staged assets FIRST (validated + normalized server-side), then the
      // spec that references them. If an upload is rejected, we never set the spec.
      // The two slots are independent files, so they go up CONCURRENTLY — running
      // them in series just added the whole second request's latency to Save for
      // no ordering benefit. Promise.all still rejects on the first failure, so
      // the "a rejected upload never sets the spec" invariant is unchanged.
      //
      // Gated on what the SPEC references, not merely on what's staged: the user
      // can pick an image and then switch the slot to a colour/text, and that
      // abandoned file must not be written into the app folder where nothing
      // points at it. This is what lets a staged pick survive tab-toggling.
      const spec = parsed.data
      await Promise.all([
        stagedBg && spec.bg.type === 'image' && miniappApi.setIconAsset(file.id, 'bg', stagedBg.file),
        stagedFg && spec.image === true && miniappApi.setIconAsset(file.id, 'fg', stagedFg.file),
      ])
      await miniappApi.setIcon(file.id, spec)
      toast.success(t('iconEditor.saved'))
      onSaved?.(parsed.data)
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('iconEditor.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    setSaving(true)
    try {
      await miniappApi.setIcon(file.id, null)
      toast.success(t('iconEditor.resetDone'))
      onSaved?.(null)
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('iconEditor.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const segBtn = (active: boolean) =>
    `flex-1 px-2.5 py-1 text-xs rounded-md transition-colors ${active ? 'ws-glass-chip themed-text font-medium' : 'themed-text-muted hover:themed-text'}`

  // Preview: ALWAYS composite locally, never the server PNG. The server PNG is the
  // SAVED render — its `rev` can't change while you edit, so falling back to it
  // froze the preview: switching the foreground to text/icon/none updated the draft
  // but redrew the identical stale image, which read as "I can't change it".
  // Staged (just-picked) beats saved; a slot with neither draws the spec's own
  // background. MiniappIcon gates each `<image>` on the DRAFT (`bg.type === 'image'`
  // / `image === true`), so a slot the draft no longer uses is simply not drawn —
  // which is why passing a saved URL here can't resurrect an abandoned image.
  const previewAssets = {
    bg: stagedBg?.url ?? savedAssets.bg,
    fg: stagedFg?.url ?? savedAssets.fg,
  }
  // No `refId`/`rev`: passing `previewAssets` opts out of the server PNG, so those
  // would be dead props that merely imply the stale-render path might still engage.
  const previewProps = { name: file.name, previewAssets }

  const hasColorControl = fgTab === 'text' || fgTab === 'icon'

  /** An image slot's source row: what's currently chosen, plus the two ways to
   *  change it. Selecting the Image TAB no longer fires the OS file dialog — the
   *  user lands here and picks a source deliberately. */
  const assetSource = (kind: 'bg' | 'fg') => {
    const staged = kind === 'bg' ? stagedBg : stagedFg
    const saved = kind === 'bg' ? savedAssets.bg : savedAssets.fg
    const inputRef = kind === 'bg' ? bgFileRef : fgFileRef
    const busy = driveLoading || staging[kind]
    const btn = 'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg ws-glass-well themed-text-secondary hover:themed-text hover:ws-glass-chip text-xs transition-colors disabled:opacity-40'
    return (
      <>
        <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg ws-glass-well mb-1.5">
          {staging[kind]
            ? <Loader2 size={16} className="shrink-0 themed-text-muted animate-spin" />
            : <ImageIcon size={16} className="shrink-0 themed-text-muted" />}
          <span className="text-sm truncate flex-1 themed-text">
            {staging[kind]
              ? t('iconEditor.resizing', { defaultValue: 'Resizing…' })
              : staged
                ? staged.file.name
                : saved
                  ? t('iconEditor.currentImage', { defaultValue: 'Current image' })
                  : t('iconEditor.noImageYet', { defaultValue: 'No image chosen' })}
          </span>
        </div>
        <div className="flex gap-1.5">
          <button className={btn} disabled={busy} onClick={() => inputRef.current?.click()}>
            <Upload size={14} className="shrink-0" />
            {t('iconEditor.fromComputer', { defaultValue: 'Computer' })}
          </button>
          {/* Hidden without a tenant — see the `tenantId` prop note. */}
          {tenantId && (
            <button className={btn} disabled={busy} onClick={() => setDrivePicker(kind)}>
              {driveLoading
                ? <Loader2 size={14} className="shrink-0 animate-spin" />
                : <HardDrive size={14} className="shrink-0" />}
              {/* The default interpolates too, so a missing key degrades to the
                  brand's own name rather than to a hardcoded "Papan Drive". */}
              {t('iconEditor.fromDrive', { defaultValue: '{{productName}} Drive' })}
            </button>
          )}
        </div>
      </>
    )
  }

  /** One preview tile. While the saved assets are still loading, an asset-backed
   *  icon has nothing real to draw — MiniappIcon would fall back to the
   *  name-derived gradient, which flashes a wrong icon for a beat and reads as
   *  "my icon is broken". Show a spinner in the same box instead (no layout jump);
   *  spec-only icons never enter this state and paint instantly as before. */
  const previewIcon = (size: number) =>
    savedLoading ? (
      <div style={{ width: size, height: size }} className="flex items-center justify-center">
        <Loader2 size={Math.max(12, Math.min(size - 4, 24))} className="animate-spin themed-text-muted" />
      </div>
    ) : (
      <MiniappIcon spec={draft} {...previewProps} size={size} />
    )

  /** Helper-text line under a file picker: the rejection if there is one, else the hint. */
  const assetHelp = (error: string | null, hint: string) => (
    <div
      className={`text-[11px] mt-1 ${error ? 'text-red-400' : 'themed-text-muted'}`}
      role={error ? 'alert' : undefined}
    >
      {error ?? hint}
    </div>
  )

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      {/* A column, not one scrolling box: the panel is taller than a laptop
          viewport (there are eight sections), and scrolling the WHOLE panel
          took the title and the Save bar off screen with it — the two things
          that must never move. Header and footer hold; only the middle
          scrolls. */}
      {/* Glass dialog (`ws-glass-modal`, DESIGN_DOC §10 — as NewProjectDialog): inner controls sit
          on glass wells/chips, no drawn borders; the footer is the glass footer strip. */}
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative z-10 w-[400px] max-w-[92vw] max-h-[85vh] flex flex-col`}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
          <div className="min-w-0">
            {/* "Edit icon" is about an app's own tile. Produce mode is designing
                one for a page, and the author got here from "Design one". */}
            <h3 className="text-sm font-semibold themed-text truncate">{onProduce ? t('iconEditor.designTitle') : t('iconEditor.title')}</h3>
            {/* The subtitle names the app whose icon this is. In produce mode the
                icon belongs to the author's page, not to any app, so naming one
                would be a lie. */}
            {!onProduce && <p className="text-xs themed-text-muted truncate" title={displayName}>{displayName}</p>}
          </div>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded-lg ws-glass-well shrink-0" aria-label={t('iconEditor.close')}>
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-4 space-y-3 overflow-y-auto min-h-0 flex-1">
          {/* ── Live preview: light and dark, and the two sizes it is really
                 seen at, on ONE row. This was two rows and 184px tall — a
                 third of the panel spent on a picture of a 40px tile, which is
                 what made an eight-section form need scrolling to reach its
                 own Save button. ────────────────────────────────────────── */}
          <div className="flex items-center justify-center gap-2.5">
            <div className="rounded-xl p-2.5 flex items-center justify-center" style={{ background: '#f2f2f7' }}>
              {previewIcon(72)}
            </div>
            <div className="rounded-xl p-2.5 flex items-center justify-center" style={{ background: '#1c1c1e' }}>
              {previewIcon(72)}
            </div>
            <div className="flex flex-col items-center justify-center gap-2 pl-1">
              {previewIcon(40)}
              {previewIcon(20)}
            </div>
          </div>

          {/* Hidden file pickers for image assets. */}
          <input ref={bgFileRef} type="file" accept={MINIAPP_ICON_BG_ACCEPT} className="hidden" onChange={onPickFile('bg')} />
          <input ref={fgFileRef} type="file" accept={MINIAPP_ICON_FG_ACCEPT} className="hidden" onChange={onPickFile('fg')} />

          {/* ── Foreground mode: Text | Icon | Image | Blank ──────────── */}
          <div>
            <div className="text-xs themed-text-muted mb-1">{t('iconEditor.foregroundLabel')}</div>
            <div className="flex gap-1 ws-glass-well p-0.5 rounded-lg">
              <button className={segBtn(fgTab === 'text')} onClick={() => setFgMode('text')}>{t('iconEditor.modeText')}</button>
              <button className={segBtn(fgTab === 'icon')} onClick={() => setFgMode('icon')}>{t('iconEditor.modeIcon')}</button>
              <button className={segBtn(fgTab === 'image')} onClick={() => setFgMode('image')}>{t('iconEditor.modeImage', 'Image')}</button>
              <button className={segBtn(fgTab === 'blank')} onClick={() => setFgMode('blank')}>{t('iconEditor.modeBlank', 'None')}</button>
            </div>
          </div>

          {fgTab === 'text' ? (
            <>
              {/* ── Text ──────────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs themed-text-muted">{t('iconEditor.textLabel')}</label>
                  <span className={`text-[11px] ${overLimit ? 'text-red-400' : 'themed-text-muted'}`}>{gcount}/{MINIAPP_ICON_TEXT_MAX_GRAPHEMES}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={draft.text ?? ''}
                    onChange={(e) => setText(e.target.value)}
                    className="themed-input ws-glass-well border-0 flex-1 text-sm px-2 py-1.5 rounded-lg"
                    spellCheck={false}
                    placeholder={t('iconEditor.textPlaceholder')}
                    aria-label={t('iconEditor.textLabel')}
                  />
                  <button
                    ref={emojiBtnRef}
                    onClick={() => setEmojiOpen((v) => !v)}
                    className={`p-2 rounded-lg transition-colors ${emojiOpen ? 'ws-glass-chip themed-text' : 'ws-glass-well themed-text-secondary hover:themed-text'}`}
                    title={t('iconEditor.emojiButton')}
                    aria-label={t('iconEditor.emojiButton')}
                  >
                    <Smile size={16} />
                  </button>
                </div>
              </div>

              {/* ── Emoji style ───────────────────────────────────────── */}
              <div>
                <div className="text-xs themed-text-muted mb-1">{t('iconEditor.emojiStyleLabel')}</div>
                <div className="flex gap-1 ws-glass-well p-0.5 rounded-lg">
                  <button className={segBtn(draft.emojiStyle === 'color')} onClick={() => setDraft((d) => ({ ...d, emojiStyle: 'color' }))}>{t('iconEditor.emojiColor')}</button>
                  <button className={segBtn(draft.emojiStyle === 'mono')} onClick={() => setDraft((d) => ({ ...d, emojiStyle: 'mono' }))}>{t('iconEditor.emojiMono')}</button>
                </div>
              </div>
            </>
          ) : fgTab === 'icon' ? (
            /* ── Lucide icon ─────────────────────────────────────────── */
            <div>
              <div className="text-xs themed-text-muted mb-1">{t('iconEditor.iconLabel')}</div>
              <button
                ref={iconBtnRef}
                onClick={() => setIconOpen((v) => !v)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg transition-colors ${iconOpen ? 'ws-glass-chip themed-text' : 'ws-glass-well themed-text-secondary hover:themed-text'}`}
              >
                <Shapes size={16} className="shrink-0" />
                <span className="text-sm font-mono truncate flex-1 text-left">{draft.icon ?? MINIAPP_ICON_DEFAULT_LUCIDE}</span>
                <span className="text-xs themed-text-muted shrink-0">{t('iconEditor.chooseIcon')}</span>
              </button>
            </div>
          ) : fgTab === 'image' ? (
            /* ── Uploaded foreground image (SVG/PNG) ─────────────────── */
            <div>
              <div className="text-xs themed-text-muted mb-1">{t('iconEditor.foregroundImageLabel', 'Foreground image')}</div>
              {assetSource('fg')}
              {/* Names the SVG cap but not the raster one: a raster is shrunk to
                  the render target before upload, so its byte cap is unreachable
                  and quoting it would just invite "why was my 3 MB photo fine?".
                  An SVG is vector — it can't be shrunk, so its 256 KB is real. */}
              {assetHelp(
                assetError.fg,
                t('iconEditor.foregroundImageHint', {
                  defaultValue: 'SVG up to {{svg}}. PNG or JPEG is resized to {{dim}}px.',
                  svg: svgLabel,
                  dim: MINIAPP_ICON_FG_RENDER_DIM,
                }),
              )}
            </div>
          ) : (
            /* ── Blank (background only) ──────────────────────────────── */
            <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg ws-glass-well themed-text-muted text-xs">
              <Ban size={14} className="shrink-0" />
              {t('iconEditor.blankHint', 'No foreground — the background shows on its own.')}
            </div>
          )}

          {/* ── Glyph / text color (only for a text/icon foreground) ───── */}
          {hasColorControl && (
            <div>
              <div className="text-xs themed-text-muted mb-1">{fgTab === 'icon' ? t('iconEditor.iconColorLabel') : t('iconEditor.textColorLabel')}</div>
              <div className="flex items-center gap-2">
                <ColorField color={glyphColor} onChange={setTextColor} themeClass={themeClass} />
                <span className="text-xs themed-text-muted font-mono">{(draft.textColor ?? glyphColor).toLowerCase()}</span>
                <div className="flex-1" />
                <label className="flex items-center gap-1.5 text-sm themed-text cursor-pointer select-none" title={t('iconEditor.autoHint')}>
                  <input type="checkbox" checked={autoText} onChange={(e) => setAutoText(e.target.checked)} />
                  {t('iconEditor.auto')}
                </label>
              </div>
            </div>
          )}

          {/* ── Background ─────────────────────────────────────────────── */}
          <div>
            <div className="text-xs themed-text-muted mb-1">{t('iconEditor.backgroundLabel')}</div>
            <div className="flex gap-1 ws-glass-well p-0.5 rounded-lg mb-2">
              <button className={segBtn(bgTab === 'solid')} onClick={() => setBgType('solid')}>{t('iconEditor.solid')}</button>
              <button className={segBtn(bgTab === 'gradient')} onClick={() => setBgType('gradient')}>{t('iconEditor.gradient')}</button>
              <button className={segBtn(bgTab === 'image')} onClick={() => setBgType('image')}>{t('iconEditor.modeImage', 'Image')}</button>
            </div>
            {/* The `draft.bg.type` half of each test is what narrows the union for
                TS. It also does real work: on the Image tab with no image chosen
                yet the draft is still solid/gradient, so both tests fall through
                to the image panel below. */}
            {bgTab === 'solid' && draft.bg.type === 'solid' ? (
              <div className="flex items-center gap-2">
                <ColorField color={draft.bg.color} onChange={setSolid} themeClass={themeClass} />
                <span className="text-xs themed-text-muted font-mono">{draft.bg.color}</span>
              </div>
            ) : bgTab === 'gradient' && draft.bg.type === 'gradient' ? (
              <div className="flex items-center gap-4">
                <ColorField label={t('iconEditor.top')} color={draft.bg.from} onChange={(c) => setGrad({ from: c })} themeClass={themeClass} />
                <ColorField label={t('iconEditor.bottom')} color={draft.bg.to} onChange={(c) => setGrad({ to: c })} themeClass={themeClass} />
              </div>
            ) : (
              <div>
                {assetSource('bg')}
                {assetHelp(
                  assetError.bg,
                  t('iconEditor.backgroundImageHint', {
                    defaultValue: 'PNG or JPEG. Resized to {{dim}}px tall and cropped to the tile shape.',
                    dim: MINIAPP_ICON_BG_RENDER_DIM,
                  }),
                )}
              </div>
            )}
            {/* Presets (gradient/solid only) */}
            {bgTab !== 'image' && <div className="flex flex-wrap gap-1.5 mt-2">
              {presets.map((p, i) => (
                <button
                  key={i}
                  onClick={() => applyPreset(p)}
                  className="w-7 h-7 rounded-lg shrink-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)] transition-transform hover:scale-110"
                  style={{ background: `linear-gradient(180deg, ${p.from}, ${p.to})` }}
                  title={`${p.from} → ${p.to}`}
                  aria-label={t('iconEditor.applyPreset')}
                />
              ))}
            </div>}
          </div>

        </div>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <div className="ws-glass-footer flex items-center gap-2 px-5 py-3 shrink-0 min-w-0">
          {/* Reset DELETES the saved icon. In produce mode nothing is saved (and
              `file.id` is a placeholder), so the control has nothing to act on —
              it would only ever produce an error toast. */}
          {!onProduce && (
            <button
              onClick={handleReset}
              disabled={saving}
              className="text-xs themed-text-muted hover:themed-text disabled:opacity-40"
            >
              {t('iconEditor.reset')}
            </button>
          )}
          {/* Bottom-LEFT of the modal, opposite the commit buttons: it qualifies
              the action rather than being one. */}
          {onProduce && placement && placement.options.length > 0 && (
            <PlacementPicker
              label={placement.label}
              options={placement.options}
              value={placement.value}
              onChange={placement.onChange}
              disabled={saving}
            />
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            disabled={saving}
            className="shrink-0 px-3 py-1.5 text-sm themed-text-muted hover:themed-text rounded-lg ws-glass-well transition-colors disabled:opacity-40"
          >
            {t('iconEditor.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 text-sm themed-btn-primary rounded-lg transition-colors disabled:opacity-40"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {/* In produce mode the button does not SAVE anything — it hands the
                design back — so it must not say "Save". */}
            {onProduce ? t('iconEditor.use') : t('iconEditor.save')}
          </button>
        </div>
      </div>

      {/* ── Glass popovers (portaled; anchored to their triggers) ──────── */}
      {drivePicker && tenantId && (
        <IconAssetDrivePicker
          tenantId={tenantId}
          kind={drivePicker}
          onPick={(sel) => void pickFromDrive(drivePicker, sel)}
          onClose={() => setDrivePicker(null)}
        />
      )}
      {emojiOpen && fgTab === 'text' && (
        <EmojiPickerPopover
          anchorRef={emojiBtnRef}
          onClose={() => setEmojiOpen(false)}
          onSelect={(native) => insertEmoji(native)}
          emojiStyle={draft.emojiStyle}
          themeClass={themeClass}
          searchPlaceholder={t('iconEditor.emojiSearch')}
          noResults={t('iconEditor.noResults')}
          categoryLabels={emojiCategoryLabels}
        />
      )}
      {iconOpen && fgTab === 'icon' && (
        <IconPickerPopover
          anchorRef={iconBtnRef}
          onClose={() => setIconOpen(false)}
          onSelect={(name) => { setIconName(name); setIconOpen(false) }}
          selected={draft.icon}
          themeClass={themeClass}
          searchPlaceholder={t('iconEditor.iconSearch')}
          noResults={t('iconEditor.noResults')}
        />
      )}
    </div>,
    document.body,
  )
}
